// M-Pesa payouts from the bulk account until the direct API is live:
// 1) put approved requests in a batch and download the bulk file, 2) upload it in the M-Pesa portal,
// 3) upload the M-Pesa statement - payments are matched to requests and anything unapproved is listed.
import { useState } from "react";
import { Page, Card, Btn, Tabs, Table, Tr, Td, Loading, Pill, money } from "@/components/kit";
import api from "@/lib/api";
import { act, useLoad, useCan } from "@/components/erp";
import { download } from "@/lib/download";
import { fmtDate } from "@/lib/money";

function Funds() {
  const can = useCan();
  const { data, reload } = useLoad("/payouts/balance");
  const mode = useLoad("/payouts/api-status");
  const [busy, setBusy] = useState(false);
  if (!data) return null;
  const refresh = async () => {
    setBusy(true);
    if (await act(() => api.post("/payouts/balance/refresh"), "Asked M-Pesa - the balance appears in a few seconds")) setTimeout(reload, 6000);
    setBusy(false);
  };
  return (
    <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm">
      <div><span className="text-slate-500">Float in the ERP</span> <b className="tabular-nums">{money(data.float)}</b></div>
      <div><span className="text-slate-500">Approved, not yet paid</span> <b className="tabular-nums">{money(data.approved_unpaid)}</b></div>
      {data.enabled && (
        <div><span className="text-slate-500">M-Pesa account</span>{" "}
          <b className="tabular-nums">{data.mpesa == null ? "not checked yet" : money(data.mpesa)}</b>
          {data.as_at && <span className="text-slate-500"> as at {new Date(data.as_at).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" })}</span>}
          {" "}<Btn onClick={refresh} disabled={busy}>Check balance</Btn></div>)}
      {mode.data?.available && (
        <div className="basis-full text-slate-600">
          Payments go out by: <b>{mode.data.enabled ? "direct from the ERP" : "the bulk file (portal)"}</b>
          {can("approve.final") && <> {" "}<Btn onClick={async () => { if (await act(() => api.post("/payouts/direct", { on: !mode.data.enabled }), mode.data.enabled ? "Back to the bulk file" : "Direct payment is on")) mode.reload(); }}>
            {mode.data.enabled ? "Use the bulk file" : "Switch on direct payment"}</Btn></>}
        </div>)}
    </div>
  );
}

export default function Payouts() {
  const [tab, setTab] = useState("ready");
  return (
    <Page title="M-Pesa payouts">
      <Funds />
      <Tabs value={tab} onChange={setTab} tabs={[{ value: "ready", label: "Ready to pay" }, { value: "batches", label: "Batches" }, { value: "statement", label: "Statement & checks" }]} />
      {tab === "ready" && <Ready goBatches={() => setTab("batches")} />}
      {tab === "batches" && <Batches />}
      {tab === "statement" && <Statement />}
    </Page>
  );
}

function Ready({ goBatches }) {
  const { data, reload } = useLoad("/payouts/ready");
  const bal = useLoad("/payouts/balance");
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  if (!data) return <Loading />;
  const sum = data.filter((r) => picked.includes(r.id)).reduce((s, r) => s + r.amount, 0);
  const make = async () => {
    setBusy(true);
    const b = await act(() => api.post("/payouts/batches", { expense_ids: picked }), (d) => `Batch ${d.no} made - download its file`);
    setBusy(false);
    if (b) { setPicked([]); reload(); goBatches(); }
  };
  if (!data.length) return <p className="mt-6 text-sm text-slate-500">Nothing approved is waiting to be paid from M-Pesa.</p>;
  return (
    <>
      <p className="mt-3 text-sm text-slate-600">The money goes to the number on the payee's record - nobody types a number here. Whoever approved a request can't put it in a batch.</p>
      <Table minWidth={860} cols={[{ label: "" }, { label: "Date" }, { label: "What" }, { label: "Truck" }, { label: "Pay to" }, { label: "Amount", align: "right" }, { label: "" }]}>
        {data.map((r) => (
          <Tr key={r.id}>
            <Td><input type="checkbox" className="h-5 w-5" aria-label="Select" disabled={!!r.problem} checked={picked.includes(r.id)}
              onChange={(e) => setPicked(e.target.checked ? [...picked, r.id] : picked.filter((x) => x !== r.id))} /></Td>
            <Td>{fmtDate(r.date)}</Td>
            <Td>{r.category}{r.description ? ` - ${r.description}` : ""}</Td>
            <Td>{r.truck}</Td>
            <Td>{r.payee ? `${r.payee} · ${r.phone || "no number"}` : "—"}</Td>
            <Td right>{money(r.amount)}</Td>
            <Td>{r.problem && <Pill kind="red">{r.problem}</Pill>}</Td>
          </Tr>))}
      </Table>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Btn kind="primary" disabled={!picked.length || busy} onClick={make}>Make a batch ({picked.length}) · {money(sum)}</Btn>
        {bal.data && sum > bal.data.float && <span className="text-sm font-medium text-rose-700">More than the float holds ({money(bal.data.float)}) - top the float up first</span>}
        {bal.data?.mpesa != null && sum > bal.data.mpesa && <span className="text-sm font-medium text-rose-700">More than the M-Pesa account held at the last check ({money(bal.data.mpesa)})</span>}
      </div>
    </>
  );
}

function Batches() {
  const { data, reload } = useLoad("/payouts/batches");
  const st = useLoad("/payouts/api-status");
  const can = useCan();
  const [busy, setBusy] = useState(false);
  const send = async (b) => {
    if (!window.confirm(`Pay ${b.no} (${money(b.total)}) straight from M-Pesa now? This sends the money.`)) return;
    setBusy(true);
    await act(() => api.post(`/payouts/batches/${b.id}/send`), (d) => `${d.sent} sent to M-Pesa${d.failed ? `, ${d.failed} failed` : ""}`);
    setBusy(false);
    reload();
  };
  if (!data) return <Loading />;
  if (!data.length) return <p className="mt-6 text-sm text-slate-500">No batches yet.</p>;
  return (
    <Table minWidth={760} cols={[{ label: "Batch" }, { label: "Made" }, { label: "Payments" }, { label: "Total", align: "right" }, { label: "Status" }, { label: "" }]}>
      {data.map((b) => (
        <Tr key={b.id}>
          <Td className="font-medium">{b.no}</Td>
          <Td>{fmtDate((b.created_at || "").slice(0, 10))}</Td>
          <Td>{b.paid} of {b.count} paid{b.sent ? ` · ${b.sent} with M-Pesa` : ""}{b.failed ? ` · ${b.failed} failed` : ""}{b.timeout ? ` · ${b.timeout} unconfirmed - check the statement` : ""}</Td>
          <Td right>{money(b.total)}</Td>
          <Td><Pill kind={b.status === "paid" ? "green" : b.status === "cancelled" ? "grey" : "amber"}>{b.status === "open" ? "waiting for the statement" : b.status}</Pill></Td>
          <Td className="space-x-2 whitespace-nowrap">
            {b.status === "open" && st.data?.enabled && (!b.confidential || can("payroll.view")) && <Btn kind="primary" disabled={busy} onClick={() => send(b)}>Pay now via M-Pesa</Btn>}
            {b.status !== "cancelled" && (!b.confidential || can("payroll.view")) && <Btn onClick={() => download(`/payouts/batches/${b.id}/file`)}>Download file (XML)</Btn>}
            {b.confidential && !can("payroll.view") && <span className="text-xs text-slate-500">Salaries - prepared and downloaded by Finance</span>}
            {b.status === "open" && <Btn onClick={async () => { if (window.confirm(`Cancel ${b.no}? Unpaid requests go back to waiting.`) && await act(() => api.post(`/payouts/batches/${b.id}/cancel`), "Cancelled")) reload(); }}>Cancel</Btn>}
          </Td>
        </Tr>))}
    </Table>
  );
}

function Statement() {
  const rec = useLoad("/payouts/recon");
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    setBusy(true);
    await act(() => api.post("/payouts/statement", fd), (d) => `${d.new} new lines; ${d.matched} payments matched to requests`);
    setBusy(false);
    e.target.value = "";
    rec.reload();
  };
  const d = rec.data;
  const lines = (rows) => (
    <Table minWidth={700} cols={[{ label: "Receipt" }, { label: "When" }, { label: "Details" }, { label: "Amount", align: "right" }]}>
      {rows.map((r) => <Tr key={r.receipt}><Td>{r.receipt}</Td><Td>{(r.at || "").slice(0, 16).replace("T", " ")}</Td><Td>{r.details}</Td><Td right>{money(r.withdrawn)}</Td></Tr>)}
    </Table>);
  return (
    <>
      <Card title="Upload the M-Pesa statement" className="mt-4">
        <p className="text-sm text-slate-600">Download the account statement (CSV or Excel) from the M-Pesa portal and upload it here. Each payment is matched to its request and marked paid with the receipt number; uploading the same statement twice changes nothing.</p>
        <input type="file" accept=".csv,.xlsx,.xls" disabled={busy} onChange={upload} className="mt-3 text-sm" />
      </Card>
      {!d ? <Loading /> : (
        <>
          <Card title={`Paid without approval (${d.unapproved.length}) - ${money(d.totals.unapproved)}`} className="mt-4">
            {d.unapproved.length ? lines(d.unapproved) : <p className="text-sm text-slate-500">Every payment on the statements has an approved request behind it.</p>}
          </Card>
          <Card title={`Paid in the ERP but not on a statement yet (${d.not_on_statement.length})`} className="mt-4">
            {d.not_on_statement.length ? (
              <Table minWidth={560} cols={[{ label: "Date" }, { label: "What" }, { label: "Amount", align: "right" }]}>
                {d.not_on_statement.map((r) => <Tr key={r.id}><Td>{fmtDate(r.date)}</Td><Td>{r.category}{r.description ? ` - ${r.description}` : ""}</Td><Td right>{money(r.amount)}</Td></Tr>)}
              </Table>) : <p className="text-sm text-slate-500">Nothing outstanding.</p>}
          </Card>
          <Card title={`Matched (${d.matched.length}) - ${money(d.totals.matched)}; M-Pesa charges ${money(d.totals.charges)}`} className="mt-4">
            {d.matched.length ? lines(d.matched) : <p className="text-sm text-slate-500">None yet.</p>}
          </Card>
        </>
      )}
    </>
  );
}
